/**
 * HighRiskSitesWidget — ranks sites by safety risk, migrated from the Corporate
 * Safety Dashboard. Uses the framework's /v2/comparison endpoint (auto-resolves
 * the user's accessible sites, RBAC + time/dimension aware) and flags/orders
 * sites above a TRIR threshold. Cross-site by nature (ignores single-site pick).
 */
import React from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchComparison } from '../api/client';
import { CARD, HEADING } from '../ui';

const TRIR_THRESHOLD = 5; // matches the legacy Corporate dashboard flag
const METRICS = ['safety.trir', 'safety.ltifr'];

export default function HighRiskSitesWidget({ title = 'High Risk Sites', threshold = TRIR_THRESHOLD }) {
  const { module, time, dimensions, standard, refreshMs } = useDashboard();
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { module, metrics: METRICS };
      if (time) body.time = time;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (standard) body.standard = standard;
      return fetchComparison(body, { signal });
    },
    [module, timeKey, dimsKey, standard],
    { enabled: !!module, refreshMs },
  );

  const sites = (data?.sites || [])
    .map((s) => ({
      name: s.site_name || s.site_id,
      trir: Number(s.values?.['safety.trir']) || 0,
      ltifr: Number(s.values?.['safety.ltifr']) || 0,
    }))
    .filter((s) => s.trir > threshold)
    .sort((a, b) => b.trir - a.trir);

  return (
    <div className={`${CARD} p-4`}>
      <h3 className={`${HEADING} mb-3 flex items-center gap-2`}>
        <AlertTriangle className="h-4 w-4 text-red-500" /> {title}
      </h3>

      {loading && <div className="h-24 animate-pulse rounded bg-gray-100" />}
      {!loading && error && <div className="text-sm text-red-600">Unavailable</div>}
      {!loading && !error && sites.length === 0 && (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          No sites above the risk threshold (TRIR &gt; {threshold}).
        </div>
      )}
      {!loading && !error && sites.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {sites.map((s) => (
            <li key={s.name} className="flex items-center justify-between py-2">
              <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                {s.name}
              </span>
              <span className="text-xs text-gray-500">
                TRIR <span className="font-semibold text-red-600">{s.trir.toFixed(2)}</span>
                <span className="mx-1 text-gray-300">·</span>
                LTIFR {s.ltifr.toFixed(2)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
